import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Save, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSiteContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [content, setContent] = useState({
    hero_tag: { en: '', ru: '', kz: '' },
    hero_title: { en: '', ru: '', kz: '' },
    hero_subtitle: { en: '', ru: '', kz: '' },
    hero_description: { en: '', ru: '', kz: '' },
    hero_cta_button: { en: '', ru: '', kz: '' },
    best_gift_title: { en: '', ru: '', kz: '' },
    best_gift_subtitle: { en: '', ru: '', kz: '' },
    how_it_works_title: { en: '', ru: '', kz: '' },
    step1_title: { en: '', ru: '', kz: '' },
    step1_desc: { en: '', ru: '', kz: '' },
    step2_title: { en: '', ru: '', kz: '' },
    step2_desc: { en: '', ru: '', kz: '' },
    step3_title: { en: '', ru: '', kz: '' },
    step3_desc: { en: '', ru: '', kz: '' },
    step4_title: { en: '', ru: '', kz: '' },
    step4_desc: { en: '', ru: '', kz: '' },
    cta_title: { en: '', ru: '', kz: '' },
    cta_subtitle: { en: '', ru: '', kz: '' },
    cta_button: { en: '', ru: '', kz: '' }
  });

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching site content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (field, lang, value) => {
    setContent(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, content);
      alert('✅ Site content updated successfully! Changes will appear on the homepage.');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('❌ Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E88E5]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black">Homepage Content</h1>
            <p className="text-gray-600 mt-2">Edit homepage text for all languages</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>

        {/* Language Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('en')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'en' 
                ? 'text-[#1E88E5] border-[#1E88E5]' 
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            English
          </button>
          <button
            onClick={() => setActiveTab('ru')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'ru' 
                ? 'text-[#1E88E5] border-[#1E88E5]' 
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Русский
          </button>
          <button
            onClick={() => setActiveTab('kz')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'kz' 
                ? 'text-[#1E88E5] border-[#1E88E5]' 
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Қазақша
          </button>
        </div>

        <div className="space-y-8">
          {/* Hero Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-black mb-4">Hero Section</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tag/Badge Text ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.hero_tag[activeTab] || ''}
                  onChange={(e) => handleUpdate('hero_tag', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., 🎁 Perfect for Students & Researchers"
                />
                <p className="text-xs text-gray-500 mt-1">Small badge shown above the main title</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Main Title ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.hero_title[activeTab] || ''}
                  onChange={(e) => handleUpdate('hero_title', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Gift"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.hero_subtitle[activeTab] || ''}
                  onChange={(e) => handleUpdate('hero_subtitle', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Learning and Discovery"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description ({activeTab.toUpperCase()})
                </label>
                <textarea
                  value={content.hero_description[activeTab] || ''}
                  onChange={(e) => handleUpdate('hero_description', activeTab, e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Send beautiful digital gift cards for MethodMate subscriptions..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  CTA Button Text ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.hero_cta_button[activeTab] || ''}
                  onChange={(e) => handleUpdate('hero_cta_button', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Browse Gift Cards"
                />
              </div>
            </div>
          </div>

          {/* Best Gift For Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-black mb-4">"Best Gift For" Section</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Section Title ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.best_gift_title[activeTab] || ''}
                  onChange={(e) => handleUpdate('best_gift_title', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., The Best Gift For"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.best_gift_subtitle[activeTab] || ''}
                  onChange={(e) => handleUpdate('best_gift_subtitle', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Perfect for the academics and researchers in your life"
                />
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-black mb-4">"How It Works" Section</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Section Title ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.how_it_works_title[activeTab] || ''}
                  onChange={(e) => handleUpdate('how_it_works_title', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., How It Works"
                />
              </div>

              {/* Step 1 */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Step 1 - Choose a Design</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step1_title[activeTab] || ''}
                      onChange={(e) => handleUpdate('step1_title', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Choose a Design"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step1_desc[activeTab] || ''}
                      onChange={(e) => handleUpdate('step1_desc', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Select from beautiful templates..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Step 2 - Personalize</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step2_title[activeTab] || ''}
                      onChange={(e) => handleUpdate('step2_title', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Personalize"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step2_desc[activeTab] || ''}
                      onChange={(e) => handleUpdate('step2_desc', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Add your personal message..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Step 3 - Secure Payment</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step3_title[activeTab] || ''}
                      onChange={(e) => handleUpdate('step3_title', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Secure Payment"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step3_desc[activeTab] || ''}
                      onChange={(e) => handleUpdate('step3_desc', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Complete payment via Stripe..."
                    />
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Step 4 - Delivered</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step4_title[activeTab] || ''}
                      onChange={(e) => handleUpdate('step4_title', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Delivered"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description ({activeTab.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={content.step4_desc[activeTab] || ''}
                      onChange={(e) => handleUpdate('step4_desc', activeTab, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5]"
                      placeholder="e.g., Recipient gets email with code..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-black mb-4">Bottom CTA Section</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.cta_title[activeTab] || ''}
                  onChange={(e) => handleUpdate('cta_title', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Ready to Send a Gift?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.cta_subtitle[activeTab] || ''}
                  onChange={(e) => handleUpdate('cta_subtitle', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Choose from beautiful designs and make someone's day special"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Button Text ({activeTab.toUpperCase()})
                </label>
                <input
                  type="text"
                  value={content.cta_button[activeTab] || ''}
                  onChange={(e) => handleUpdate('cta_button', activeTab, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                  placeholder="e.g., Get Started"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-lg font-semibold"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save All Changes</span>
              </>
            )}
          </button>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Tip:</strong> After saving, refresh the homepage to see your changes. Make sure to fill in content for all 3 languages (EN, RU, KZ).
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSiteContent;
